"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import Image from "next/image"
import Link from "next/link"
import { useInView } from "react-intersection-observer"
import "swiper/css"
import "swiper/css/pagination"
import { Swiper, SwiperSlide } from "swiper/react"
import {
  Desktop,
  JobLocationIcon,
  Mobile,
  NewText,
  transitionDelayTime,
  TransitionUp,
  UnitsIcon,
  useInViewConfig,
} from "ui"
import { getNonUmbracoUrl, getUmbracoMediaUrl, ProjectPage } from "utils"
import { actionButtonClick, projectSearch } from "utils/gtm"
import { AllProjects } from "utils/projects"
import { RelatedProjects } from "utils/utils"
import blurred from "../../../ProjectsLobby/blurred64"
import WhiteBgHeading from "./WhiteBgHeading"

const ANIMATION_TIMING_FUNCTION = "cubic-bezier(0.25, 0.46, 0.45, 0.94)"

type Array = {
  key?: any
  id: number
  value: string
}[]

const StyledContainer = styled.div`
  overflow: hidden;
  display: flex;
  width: 100%;
  background-color: ${(props) => props.theme.colors.advantagesBg4rentMarketing};
  margin-bottom: -60px;
`

const StyledNewRelatedGallery = styled.section<{ inView?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-block: 60px;
  min-height: 632px;
  padding-inline: 20px;
  overflow: hidden;
  transform: ${(props) => (props.inView ? "translateX(0%)" : "translateX(200%)")};
  opacity: ${(props) => (props.inView ? 1 : 0.5)};
  transition: all 800ms ${ANIMATION_TIMING_FUNCTION};
  ${(props) => props.theme.breakpoints.up("md")} {
    min-height: 684px;
  }
`

const StyledSwiper = styled(Swiper)`
  margin: 0 auto;
  padding-block: 32px;
  overflow: visible;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding-block: 40px;
  }
`

const ActionButton = styled(Button)`
  border-radius: 8px;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0px;
  padding: 12px 28px;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: unset;
  }
`

const LinkForActionButtonBlue = styled(Link)`
  display: flex;
  width: 100%;
  text-decoration: none;
`

const ActionButtonBlue = styled(ActionButton)`
  --theme-color: ${(props) => props.theme.colors.blue4rentMarketing};
  color: white;
  background-color: var(--theme-color);
  border-color: var(--theme-color);

  padding: 12px 24px;
  line-height: 24px;

  display: flex !important;
  width: 100% !important;
  /* max-width: 328px !important; */
  width: 100% !important;

  &:hover {
    background-color: ${(props) => props.theme.colors.blue4rentMarketingHover};
  }
`

const ActionButtonWhite = styled(ActionButton)`
  --theme-color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  border: 1px solid var(--theme-color);
  color: var(--theme-color);
  background-color: white;
  &:hover {
    background-color: var(--theme-color);
    color: white;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    min-width: 328px;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  > * {
    flex: 1;
    justify-content: center;
  }
`

const StyledNewGalleryItem = styled(Link)`
  --overlay-gradient: linear-gradient(to bottom, #fff 0%, #000 100%);
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 308px;
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  &::after {
    content: "";
    display: flex;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    opacity: 0.6;
    background: var(--overlay-gradient);
  }
`

const ImageContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  flex: 1;
`

const TextContent = styled.div`
  position: absolute;
  z-index: 4;
  inset-inline-start: 0;
  inset-block-end: 0;
  padding: 24px;
  width: 100%;
  height: max-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: white;
  > * {
    margin: 0;
    padding: 0;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 16px;
  }
`
const StyledTextLine = styled.div<{ marginBottom?: string }>`
  display: flex;
  align-items: center;
  color: white;
  gap: 4px;
  margin-bottom: ${(props) => props.marginBottom || "0"};
  svg {
    width: 20px;
    height: 20px;
  }
`

const NewGalleryItem = ({
  project,
  locale,
  searchText,
  cities,
  countries,
  inView,
}: {
  project: AllProjects[number]
  locale: string
  toProject: string
  statuses: Array
  cities: Array
  countries: Array
  subsidiaries: Array
  searchText?: any
  designation?: Array
  inView?: boolean
}) => {
  const projectCity =
    project.city
      .map((status) => {
        // Find the matching status in the statuses.options
        return cities.find((option) => option.id === status.id)?.value
      })
      .filter(Boolean)?.[0] || ""

  const projectCountry =
    project.country
      .map((status) => {
        // Find the matching status in the statuses.options
        return countries.find((option) => option.id === status.id)?.value
      })
      .filter(Boolean)?.[0] || ""

  const addressParts = [projectCountry, projectCity]

  const validAddressParts = addressParts.filter((part) => part !== undefined && part !== "")
  const addressString = validAddressParts.join(", ")

  const parts = project.url!.split("/").filter((part) => part !== "")
  const lastPart = parts[parts.length - 1]

  const localePrefix = locale === "he" ? "" : "/en"
  const residentialUnits = project?.residentialUnits

  return (
    <StyledNewGalleryItem
      key={project.url}
      onClick={() => {
        actionButtonClick(project.projectTitle || "", "עבר, הווה, עתיד: הפרויקטים שלנו")
        searchText && searchText.length > 0 && projectSearch(project.projectTitle, searchText)
      }}
      href={`${localePrefix}/projects/${lastPart}`}
      style={
        {
          "--overlay-gradient": `linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, #000 100%)`,
        } as React.CSSProperties
      }
    >
      <ImageContainer>
        <Image
          src={getUmbracoMediaUrl(project.thumbnail || "/")}
          fill
          alt={project.projectTitle || ""}
          style={{ zIndex: 0, objectFit: "cover" }}
          loading={inView ? "eager" : "lazy"}
          placeholder="blur"
          blurDataURL={blurred}
          sizes="(max-width: 600px) 100vw, 450px"
        />
      </ImageContainer>
      <TextContent>
        <NewText
          component="h6"
          color="inherit"
          desktopFontSize={20}
          desktopFontWeight={600}
          desktopLineHeight={"29px"}
          style={{
            marginBottom: 4,
          }}
        >
          {project.projectTitle}
        </NewText>
        <StyledTextLine marginBottom={"8px"}>
          <JobLocationIcon width={20} height={20} color={"currentColor"} />
          <NewText
            component="p"
            desktopFontSize={16}
            desktopLineHeight={"18px"}
            color="inherit"
            desktopFontWeight={400}
          >
            {addressString}
          </NewText>
        </StyledTextLine>
        {!!residentialUnits && (
          <StyledTextLine>
            <UnitsIcon width={20} height={20} color={"currentColor"} />
            <NewText
              component="p"
              desktopFontSize={16}
              color="inherit"
              desktopFontWeight={400}
              desktopLineHeight={"100%"}
              mobileLineHeight={"100%"}
            >
              {residentialUnits} {locale === "he" ? `יח׳ דיור` : "Housing units"}
            </NewText>
          </StyledTextLine>
        )}
      </TextContent>
    </StyledNewGalleryItem>
  )
}

type Props = {
  relatedObject: RelatedProjects | undefined
  locale: string
  toProject: string
  projectData: ProjectPage
}

const NewRelatedGallery = ({ relatedObject, locale, toProject, projectData }: Props) => {
  const { ref, inView } = useInView(useInViewConfig)

  if (!relatedObject) return null
  const { cities, countries, relatedProjects, statuses, subsidiaries, designations } = relatedObject
  const title = projectData?.seeMoreTitleNew || projectData?.seeMoreTitle || ""
  const subTitle = projectData?.textUnderneathTitle
  const projectButton = projectData?.projectButton?.[0]

  return (
    <TransitionUp ref={ref} $inView={inView} $delay={transitionDelayTime}>
      <StyledContainer>
        <StyledNewRelatedGallery inView={inView}>
          <WhiteBgHeading title={title} subTitle={subTitle} />
          <StyledSwiper
            dir={locale === "he" ? "rtl" : "ltr"}
            slidesPerView={1.1}
            spaceBetween={24}
            breakpoints={{
              768: {
                slidesPerView: 2,
                spaceBetween: 32,
              },
              1366: {
                slidesPerView: 3,
              },
            }}
          >
            {relatedProjects?.map((project, index) => (
              <SwiperSlide key={index}>
                <NewGalleryItem
                  project={project}
                  countries={countries}
                  statuses={statuses}
                  subsidiaries={subsidiaries}
                  key={project.projectTitle}
                  toProject={toProject}
                  locale={locale}
                  cities={cities}
                  designation={designations}
                  inView={inView}
                />
              </SwiperSlide>
            ))}
          </StyledSwiper>
          {projectButton?.url && projectButton?.name && (
            <ButtonContainer>
              <Desktop aboveTheFold>
                <Link
                  href={getNonUmbracoUrl(projectButton.url) || "#"}
                  target={(projectButton as any)?.target || "_self"}
                  passHref
                >
                  <ActionButtonWhite variant="contained" color="primary">
                    {projectButton.name}
                  </ActionButtonWhite>
                </Link>
              </Desktop>
              <Mobile>
                <LinkForActionButtonBlue
                  href={getNonUmbracoUrl(projectButton.url) || "#"}
                  target="__blank"
                  passHref
                >
                  <ActionButtonBlue variant="contained">{projectButton.name}</ActionButtonBlue>
                </LinkForActionButtonBlue>
              </Mobile>
            </ButtonContainer>
          )}
        </StyledNewRelatedGallery>
      </StyledContainer>
    </TransitionUp>
  )
}
export default NewRelatedGallery
